// composables/useGlobalModal.ts
import { ref, readonly, inject, type InjectionKey } from 'vue'

const GlobalModalKey: InjectionKey<ReturnType<typeof createGlobalModalState>> =
  Symbol('GlobalModal')

const noOpFunction = () => {}

// Create this OUTSIDE any function to ensure it's created only once
const isOpen = ref(false)
const component = shallowRef<any>(null)
const allowClickAway = ref(true)
const modalProps = ref<Record<string, any>>({})
const onClose = ref<() => void>(noOpFunction)
const onCancel = ref<() => void>(noOpFunction)
const onConfirm = ref<() => void>(noOpFunction)
interface ModalContainerProps {
  allowClickAway?: boolean
  component: any
  modalProps?: Record<string, any>
  onClose?: () => void
  onCancel?: () => void
  onConfirm?: () => void
}

function createGlobalModalState() {
  const open = (props: ModalContainerProps) => {
    allowClickAway.value = props.allowClickAway ?? true
    component.value = props.component
    modalProps.value = { ...props?.modalProps }
    isOpen.value = true
    onClose.value = props.onClose ?? noOpFunction
    onCancel.value = props.onCancel ?? noOpFunction
    onConfirm.value = props.onConfirm ?? noOpFunction
  }

  const resetModal = () => {
    isOpen.value = false
    component.value = null
  }
  const close = () => {
    onClose.value()
    resetModal()
  }

  const cancel = () => {
    onCancel.value()
    resetModal()
  }

  const confirm = () => {
    onConfirm.value()
    resetModal()
  }

  return {
    allowClickAway: readonly(allowClickAway),
    isOpen: readonly(isOpen),
    component,
    modalProps: readonly(modalProps),
    open,
    close,
    cancel,
    confirm,
  }
}

// Singleton instance
const globalModalState = createGlobalModalState()

// Plugin
export const globalModalPlugin = {
  install(app: any) {
    app.provide(GlobalModalKey, globalModalState)
    // Add to global properties
    app.config.globalProperties.$modal = globalModalState
  },
}

// Composable
export function useGlobalModal() {
  const modal = inject(GlobalModalKey) || globalModalState
  return modal
}
